<script lang="ts">
	// The design system's Segmented (design/components/core/Segmented.jsx):
	// two to four options of one or two words, one pill that slides between
	// them. The movement is information, not decoration: one indicator, and it
	// moved. Options are equal width so the pill is placed with arithmetic, and
	// it slides on inset-inline-start, which needs no sign flip in Arabic.
	//
	// Equal columns sized to the LONGEST label: in max-content sizing a 1fr
	// track resolves to the largest item, so the row is n times the widest.
	// An even split of the track, as before, let a long label spill out of a
	// pill sized for its shorter neighbour.
	//
	// onChange fires on every tap, the chosen option included, as in the design:
	// a caller can use a second tap to reopen what the option leads to.
	//
	// A disabled option is struck, not sunk: it already sits on the well, so
	// there is no ground to take away (data-press="bare", interaction.css).
	// The chosen option is never disabled.

	import { pressable } from './pressable';

	interface Option {
		value: string;
		label: string;
		testId: string;
		/** A 7px signal dot, as a colour token. */
		dot?: string;
		disabled?: boolean;
	}

	interface Props {
		label: string;
		options: Option[];
		value: string;
		onChange: (value: string) => void;
		testId?: string;
		/** Takes the width it is given, as the design's style prop allows. */
		wide?: boolean;
	}

	let { label, options, value, onChange, testId, wide = false }: Props = $props();

	const n = $derived(options.length);
	const index = $derived(options.findIndex((option) => option.value === value));
</script>

<div class="segmented" class:wide role="group" aria-label={label} data-testid={testId}>
	<!-- The inner track is the pill's containing block, so its percentages
	     are of the content box, not the padded one. -->
	<div class="track" style:--options={n}>
		{#if index >= 0}
			<span
				class="pill"
				aria-hidden="true"
				style:inset-inline-start="calc({index} * (100% / {n}))"
				style:inline-size="calc(100% / {n})"
			></span>
		{/if}
		{#each options as option (option.value)}
			{@const on = option.value === value}
			{@const off = !!option.disabled && !on}
			<button
				type="button"
				class="option"
				class:on
				data-testid={option.testId}
				aria-pressed={on}
				disabled={off}
				data-press="bare"
				data-ground={on ? 'invert' : undefined}
				onclick={() => onChange(option.value)}
				{@attach pressable}
			>
				{#if option.dot}
					<span class="dot" aria-hidden="true" style:background={option.dot}></span>
				{/if}
				{option.label}
			</button>
		{/each}
	</div>
</div>

<style>
	.segmented {
		padding: 4px;
		background: var(--surface-well);
		border-radius: var(--radius-btn);
	}

	.wide {
		inline-size: 100%;
	}

	.track {
		position: relative;
		display: grid;
		grid-template-columns: repeat(var(--options), 1fr);
		min-inline-size: max-content;
	}

	.pill {
		position: absolute;
		inset-block: 0;
		background: var(--surface-invert);
		border-radius: 7px;
		transition: inset-inline-start var(--dur-press) var(--ease-out);
	}

	.option {
		position: relative;
		min-inline-size: 0;
		font-family: var(--font-sans);
		font-size: 13.5px;
		font-weight: 600;
		cursor: pointer;
		border: none;
		background: transparent;
		min-block-size: var(--ctl-min);
		padding: 8px 15px;
		border-radius: 7px;
		white-space: nowrap;
		text-align: center;
		color: var(--text-body);
	}

	.option.on {
		color: var(--on-ink);
	}

	.dot {
		display: inline-block;
		inline-size: 7px;
		block-size: 7px;
		border-radius: var(--radius-pill);
		margin-inline-end: 8px;
		vertical-align: middle;
	}
</style>
